import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client";
import { toast } from "sonner";
import { Send, Loader2, Plus, Trash2, Sparkles } from "lucide-react";
import logger from "../lib/logger";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AssistantTab() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  const loadSessions = useCallback(() => {
    api.get("/assistant/sessions").then(({ data }) => setSessions(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const openSession = async (id) => {
    setSessionId(id);
    try {
      const { data } = await api.get(`/assistant/sessions/${id}/messages`);
      setMessages(data || []);
    } catch (err) {
      logger.error(err);
      toast.error("Could not load conversation");
    }
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
  };

  const removeSession = async (id) => {
    try {
      await api.delete(`/assistant/sessions/${id}`);
      if (id === sessionId) newChat();
      loadSessions();
    } catch (err) {
      logger.error(err);
      toast.error("Could not delete conversation");
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: "user", content: text },
      { id: crypto.randomUUID(), role: "assistant", content: "" },
    ]);
    try {
      const res = await fetch(`${BACKEND}/api/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let newSession = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const ev = JSON.parse(line.slice(5));
          if (ev.type === "session") {
            newSession = ev.session_id;
          } else if (ev.type === "delta") {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = {
                ...next[next.length - 1],
                content: next[next.length - 1].content + ev.content,
              };
              return next;
            });
          } else if (ev.type === "error") {
            throw new Error(ev.detail);
          }
        }
      }
      if (newSession && newSession !== sessionId) {
        setSessionId(newSession);
        loadSessions();
      }
    } catch (err) {
      logger.error(err);
      toast.error(err.message || "Assistant request failed");
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="max-w-5xl flex gap-4" data-testid="assistant-tab">
      {/* Session list */}
      <aside className="w-56 shrink-0 border border-gray-300 bg-white rounded-sm p-3 h-[calc(100vh-220px)] overflow-y-auto">
        <button
          onClick={newChat}
          data-testid="assistant-new-chat-button"
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-[#002FA7] text-white text-xs font-bold rounded-sm hover:bg-[#002277] mb-3"
        >
          <Plus className="w-3.5 h-3.5" /> New chat
        </button>
        {sessions.map((s) => (
          <div
            key={s.id}
            className={`group flex items-center gap-1 px-2 py-1.5 rounded-sm cursor-pointer text-xs mb-1 ${
              s.id === sessionId ? "bg-blue-50 text-[#002FA7] font-semibold" : "hover:bg-gray-100 text-gray-700"
            }`}
            onClick={() => openSession(s.id)}
            data-testid={`assistant-session-${s.id}`}
          >
            <span className="flex-1 truncate">{s.title}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeSession(s.id);
              }}
              className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600"
              aria-label="Delete conversation"
              data-testid={`assistant-delete-session-${s.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </aside>

      {/* Chat pane */}
      <div className="flex-1 border border-gray-300 bg-white rounded-sm flex flex-col h-[calc(100vh-220px)]">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#002FA7]" />
          <div>
            <div className="font-black tracking-tight text-sm">Operations Assistant</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500">
              Claude Sonnet 4.6 · knows your live sites, team &amp; activity
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages">
          {messages.length === 0 && (
            <div className="text-sm text-gray-500 pt-8 text-center space-y-2">
              <p className="font-semibold text-gray-700">Ask anything about your operations:</p>
              <p>"How many clock-ins did we have this week?"</p>
              <p>"Which sites belong to which clients?"</p>
              <p>"Draft a message to the team about winter uniform policy."</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div
              key={m.id || `msg-${i}`}
              className={`max-w-[85%] px-3 py-2 rounded-sm text-sm whitespace-pre-wrap leading-relaxed ${
                m.role === "user"
                  ? "ml-auto bg-[#002FA7] text-white"
                  : "bg-gray-100 text-gray-900"
              }`}
              data-testid={`assistant-message-${m.role}-${i}`}
            >
              {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <div className="border-t border-gray-200 p-3 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
            placeholder="Ask the assistant…"
            disabled={streaming}
            data-testid="assistant-input"
            className="flex-1 border border-gray-300 px-3 py-2 rounded-sm text-sm focus:border-[#002FA7] outline-none"
          />
          <button
            onClick={send}
            disabled={streaming || !input.trim()}
            data-testid="assistant-send-button"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white font-bold rounded-sm hover:bg-[#002277] disabled:opacity-50"
          >
            {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
